import { useState } from "react";
import PublicLayout from "@/components/PublicLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Phone, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import WhatsAppIcon from "@/components/WhatsAppIcon";

const PHONE_E164 = "+263776162659";
const WHATSAPP_LINK = "https://wa.me/263776162659";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — we'll be in touch within one business day.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <section className="border-b border-[#E5E2DE] bg-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#E87722]">Get in touch</div>
          <h1 className="font-heading text-4xl font-bold text-ink sm:text-5xl">Speak with a real human.</h1>
          <p className="mt-4 max-w-2xl text-lg text-ink-soft">
            Questions about a product? Need help with your application? Our team responds
            within one business day.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-12 sm:px-6 lg:px-8">
          <div className="lg:col-span-5">
            <div className="space-y-6">
              <div className="rounded-2xl border border-[#E5E2DE] bg-white p-6">
                <div className="flex items-start gap-3">
                  <MapPin className="mt-1 h-5 w-5 text-[#E87722]" />
                  <div>
                    <div className="font-heading text-base font-semibold text-ink">Visit us</div>
                    <p className="mt-1 text-sm text-ink-soft">Joseph Roberts Capital<br />1 Harvey Brown Ave<br />Milton Park, Harare, Zimbabwe</p>
                  </div>
                </div>
              </div>
              <div className="rounded-2xl border border-[#E5E2DE] bg-white p-6">
                <div className="flex items-start gap-3">
                  <Phone className="mt-1 h-5 w-5 text-[#E87722]" />
                  <div>
                    <div className="font-heading text-base font-semibold text-ink">Call us</div>
                    <p className="mt-1 text-sm text-ink-soft">
                      <a href={`tel:${PHONE_E164}`} className="hover:text-[#5B2C82]" data-testid="contact-tel-link">{PHONE_E164}</a><br />
                      Mon–Fri · 08:00–17:00<br />
                      Sat · 08:00–13:00<br />
                      <span className="text-ink-muted">Closed Sundays & bank holidays</span>
                    </p>
                  </div>
                </div>
              </div>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noreferrer"
                className="block rounded-2xl border border-emerald-300 bg-emerald-50 p-6 transition-colors hover:bg-emerald-100"
                data-testid="contact-whatsapp-link"
              >
                <div className="flex items-start gap-3">
                  <WhatsAppIcon className="mt-1 h-5 w-5 text-emerald-700" />
                  <div>
                    <div className="font-heading text-base font-semibold text-emerald-900">Chat on WhatsApp</div>
                    <p className="mt-1 text-sm text-emerald-800">
                      Fastest way to reach a consultant. Tap to open WhatsApp.
                    </p>
                  </div>
                </div>
              </a>
              <div className="rounded-2xl border border-[#E5E2DE] bg-white p-6">
                <div className="flex items-start gap-3">
                  <Mail className="mt-1 h-5 w-5 text-[#E87722]" />
                  <div>
                    <div className="font-heading text-base font-semibold text-ink">Email us</div>
                    <p className="mt-1 text-sm text-ink-soft">
                      For applications:<br />
                      <a href="mailto:applications@josephrobertscapital.com" className="hover:text-[#5B2C82]" data-testid="contact-email-applications">applications@josephrobertscapital.com</a>
                    </p>
                    <p className="mt-2 text-sm text-ink-soft">
                      General enquiries:<br />
                      <a href="mailto:info@josephrobertscapital.com" className="hover:text-[#5B2C82]" data-testid="contact-email-info">info@josephrobertscapital.com</a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5 rounded-2xl border border-[#E5E2DE] bg-white p-6 lg:col-span-7 lg:p-8" data-testid="contact-form">
            <h2 className="font-heading text-2xl font-semibold text-ink">Send a message</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="contact-name" />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" data-testid="contact-phone" />
              </div>
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="contact-email" />
            </div>
            <div>
              <Label htmlFor="message">Message</Label>
              <Textarea id="message" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" data-testid="contact-message" />
            </div>
            <Button type="submit" disabled={submitting} className="bg-[#5B2C82] text-white hover:bg-[#46225f]" data-testid="contact-submit">
              {submitting ? "Sending…" : "Send message"}
            </Button>
          </form>
        </div>
      </section>
    </PublicLayout>
  );
}
